import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActiveCarePlanData,
  deleteCarePlan,
  createDraftCarePlan,
} from "../../../redux/actions/careplanActions";
import { formatDate } from "../../../utils/dateUtils";
import ActivePlanAdminView from "./ActivePlanAdminView";
import Menu from "../../common/Menu";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import TextField from "../../common/TextField";
import Pagination from "../../common/Pagination";
import CarePlanMDTUsers from "./CarePlanMDTUsers";
import FeedbackOverlay from "../../common/FeedbackOverlay";
import CarePlanAuditLogs from "../AuditLog/CarePlanAuditLogs";
import { useDebounce } from "../../../hooks/useDebounce";
import { setActiveSearchQuery } from "../../../redux/slices/careplanSlice";
import { clearGoogleTranslateCookie } from "../../../utils/googleTranslate";
import DraftCreationStatus from "../../common/popups/DraftCreationStatus";
import {
  CreateDraftIcon,
  DeleteIcon,
  EyeIcon,
  PublishIcon,
  SearchIcon,
  SortArrowIcon,
  LogIcon,
  AddUserIcon,
  ReportsIcon,
} from "../../common/Icons";
import "./CarePlans.css";

const ActiveCarePlans = () => {
  const dispatch = useDispatch();
  // Selectors for active care plans
  const activeCarePlans = useSelector(
    (state) => state.careplan.activeCarePlans
  );
  const totalRows = useSelector((state) => state.careplan.activeTotalRows);
  const activeLoading = useSelector((state) => state.careplan.activeLoading);
  const activeError = useSelector((state) => state.careplan.activeError);
  const currentPage = useSelector((state) => state.careplan.activeCurrentPage);
  const rowsPerPage = useSelector((state) => state.careplan.activeRowsPerPage);
  const activeSearchQuery = useSelector(
    (state) => state.careplan.activeSearchQuery
  );

  // Local component states
  const [addMdtUser, setAddMdtUser] = useState(false); // Control popup visibility
  const [selectedCarePlan, setSelectedCarePlan] = useState(null);
  const [viewCarePlan, setViewCarePlan] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [showCreateDraftConfirmModal, setShowCreateDraftConfirmModal] =
    useState(false);
  const [showDraftCreationStatus, setShowDraftCreationStatus] = useState(false);
  const [draftCreationStatus, setDraftCreationStatus] = useState("success");
  const [showFeedback, setShowFeedback] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Sorting state: field and order (asc/desc)
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Debounce the search input
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update filters when the debounced search input changes
  useEffect(() => {
    const newFilters = debouncedSearchInput
      ? { nhsNumber: debouncedSearchInput, createdBy: debouncedSearchInput }
      : { nhsNumber: "", createdBy: "" };

    dispatch(setActiveSearchQuery(newFilters));
  }, [debouncedSearchInput, dispatch]);

  useEffect(() => {
    // Build ordering parameter: if sortField is set, prefix with "-" if descending.
    const ordering = sortField
      ? sortOrder === "asc"
        ? sortField
        : "-" + sortField
      : "";
    dispatch(
      fetchActiveCarePlanData({
        page: currentPage,
        rowsPerPage,
        searchQuery: activeSearchQuery,
        ordering,
      })
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    activeSearchQuery,
    sortField,
    sortOrder,
  ]);

  const handlePageChange = (newPage) => {
    dispatch(
      fetchActiveCarePlanData({
        page: newPage,
        rowsPerPage,
        searchQuery: activeSearchQuery,
        ordering: sortField
          ? sortOrder === "asc"
            ? sortField
            : "-" + sortField
          : "",
      })
    );
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    dispatch(
      fetchActiveCarePlanData({
        page: 1, // reset to first page
        rowsPerPage: newRowsPerPage,
        searchQuery: activeSearchQuery,
        ordering: sortField
          ? sortOrder === "asc"
            ? sortField
            : "-" + sortField
          : "",
      })
    );
  };

  const data = activeCarePlans[currentPage] || [];
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle sort click on a header column
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if same field clicked
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Otherwise set new sort field with default ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleCreateDraftCarePlan = () => {
    if (!selectedCarePlan) {
      console.error("No care plan selected");
      return;
    }

    // Dispatch the action to create a draft
    dispatch(createDraftCarePlan(selectedCarePlan.id))
      .unwrap()
      .then((data) => {
        // Handle success
        setShowCreateDraftConfirmModal(false);
        setShowDraftCreationStatus(true);
      })
      .catch((error) => {
        setShowCreateDraftConfirmModal(false);
        setDraftCreationStatus("failure");
        setShowDraftCreationStatus(true);
        // setShowCreateDraftConfirmModal(false);
        // 'error' will be the server's response if you used rejectWithValue
        // or an AxiosError object if you didn't.
        console.error("Error creating draft:", error);
      });
  };

  const handlePrint = (plan) => {
    if (!plan?.public_id) {
      console.warn("no public_id on", plan);
      return;
    }
    clearGoogleTranslateCookie(); // Clear Google Translate cookie
    const url = `${window.location.origin}/share/${plan.public_id}/print`;
    window.open(url, "_blank");
  };

  const handleMenuClick = (carePlan, action) => {
    setSelectedCarePlan(carePlan); // Set the selected care plan
    if (action === "view") setViewCarePlan(true);
    if (action === "print") handlePrint(carePlan);
    if (action === "addUser") setAddMdtUser(true);
    if (action === "createDraft") setShowCreateDraftConfirmModal(true);
    if (action === "feedback") setShowFeedback(true);
    if (action === "delete") handleDeleteCarePlan();
    if (action === "auditLog") setShowAuditLog(true);
  };

  const handleDeleteCarePlan = () => {
    setShowDeleteConfirmModal(true);
    // dispatch(deleteCarePlan(carePlanId));
  };

  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Delete") {
      deleteTextField.setCustomValidity(
        'Please type "Delete" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }

    setDeleteText(""); //reset the delete text otherwise the next time the text box show Delete
    setShowDeleteConfirmModal(false);
    dispatch(
      deleteCarePlan({ carePlanId: selectedCarePlan.id, type: "active" })
    );
  };

  // Popup Menu Items
  const getMenuItems = (row) => [
    {
      label: "View Plan",
      icon: <EyeIcon />,
      action: () => handleMenuClick(row, "view"),
    },
    {
      label: "Print Plan",
      icon: <PublishIcon />, // or any icon you want
      action: () => handleMenuClick(row, "print"),
    },
    {
      label: "Plan Access",
      icon: <AddUserIcon />,
      action: () => handleMenuClick(row, "addUser"),
      // action: () => setAddMdtUser(true),
    },
    {
      label: "Create a draft",
      icon: <CreateDraftIcon />,
      action: () => handleMenuClick(row, "createDraft"),
    },
    {
      label: "Feedback",
      icon: <ReportsIcon />,
      action: () => handleMenuClick(row, "feedback"),
    },
    {
      label: "Audit Log",
      icon: <LogIcon />,
      action: () => handleMenuClick(row, "auditLog"),
    },
    {
      label: "Delete Plan",
      icon: <DeleteIcon />,
      action: () => handleMenuClick(row, "delete"),
    },
  ];

  if (activeLoading) {
    return <div>Loading...</div>;
  }

  if (activeError) {
    return <div>Error: There is an error please check again</div>;
  }

  return (
    <div>
      <div className="draftplan-container">
        <div className="search-filter-bar">
          <div className="search-bar">
            <div className="search-icon">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search by NHS number or Created by"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="table-wrapper">
          <table className="care-plan-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("nhs_number")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    NHS number
                    <span className="header-icon">
                      {sortField === "nhs_number" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("created_by__profile__name")}
                  style={{ cursor: "pointer" }}
                  className="hide-on-mobile"
                >
                  <div className="header-content">
                    Created by
                    <span className="header-icon">
                      {sortField === "created_by__profile__name" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("metrics__published_on")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    Published on
                    <span className="header-icon">
                      {sortField === "metrics__published_on" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("metrics__number_of_views")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    No. of views
                    <span className="muted">(current / total)</span>
                    <span className="header-icon">
                      {sortField === "metrics__number_of_views" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th className="hide-on-mobile">
                  <div className="header-content">Last viewed</div>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} className={row.locked ? "locked-row" : ""}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedCarePlan(row);
                        setViewCarePlan(true);
                        // setViewCarePlanId(row.id); // Set carePlanId when NHS number is clicked
                      }}
                    >
                      {row.nhsNumber}
                    </button>
                  </td>
                  <td className="hide-on-mobile">{row.created_by}</td>
                  <td>{formatDate(row.published_on)}</td>
                  <td>
                    <div>{row.number_of_views ?? 0}</div>
                    <div className="subtext">Total {row.total_views ?? 0}</div>
                  </td>
                  <td className="hide-on-mobile">
                    {row.last_viewed ? formatDate(row.last_viewed) : null}
                  </td>
                  <td>
                    <span className="draftplan-menu-icon">
                      <Menu menuItems={getMenuItems(row)} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Component */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
      {viewCarePlan && selectedCarePlan && (
        <ActivePlanAdminView
          carePlanId={selectedCarePlan.id}
          onClose={() => {
            setViewCarePlan(false);
            setSelectedCarePlan(null);
          }}
        />
      )}
      {addMdtUser && selectedCarePlan && (
        <FullScreenOverlay
          title="Manage Plan Access"
          popupType="add-user"
          buttons={[]}
          iconClose={true}
          onClose={() => setAddMdtUser(false)}
        >
          <CarePlanMDTUsers selectedCarePlan={selectedCarePlan} />
        </FullScreenOverlay>
      )}

      {/* Confirm Delete Care Plan Popup */}
      {showDeleteConfirmModal && (
        <FullScreenOverlay
          width="480px"
          height="300px"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteConfirmModal(false);
                setDeleteText("");
                // setItemToDelete(null);
              },
            },
            {
              label: "Yes, Delete",
              onClick: handleConfirmDelete, // Handle delete confirmation
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm Care Plan deletion</h5>
          <p>
            Are you sure you want to delete the
            <span className="confirm-delete-nhs">
              {" "}
              Care Plan {selectedCarePlan.nhsNumber}
            </span>
            ? This action cannot be undone and all the data will be permanently
            removed.
          </p>
          <p>
            Type <b>"Delete"</b> to confirm
          </p>
          <form
            id="deleteConfirmationForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              placeholder=""
              maxLength={10}
              showCharacterLimit={false}
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value); // Update state
                e.target.setCustomValidity(""); // Clear custom validation message
              }}
              autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
              required
            />
          </form>
        </FullScreenOverlay>
      )}

      {/* Confirm Create Draft Care Plan Popup (NEW) */}
      {showCreateDraftConfirmModal && selectedCarePlan && (
        <FullScreenOverlay
          width="480px"
          height="250px"
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowCreateDraftConfirmModal(false);
              },
            },
            {
              label: "Yes, Create",
              onClick: handleCreateDraftCarePlan,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Create draft care plan?</h5>
          <p>Are you sure you want to create a draft of this care plan?</p>
        </FullScreenOverlay>
      )}

      {/* Draft creation status */}
      <DraftCreationStatus
        visible={showDraftCreationStatus}
        status={draftCreationStatus} // "success" or "failure"
        nhsNumber={selectedCarePlan ? selectedCarePlan.nhsNumber : ""}
        onClose={() => setShowDraftCreationStatus(false)}
      />

      {/* Feedback Overlay */}
      {showFeedback && selectedCarePlan && (
        <FeedbackOverlay
          publicId={selectedCarePlan.public_id}
          nhsNumber={selectedCarePlan.nhsNumber}
          onClose={() => setShowFeedback(false)}
        />
      )}

      {showAuditLog && selectedCarePlan && (
        <CarePlanAuditLogs
          carePlanId={selectedCarePlan.id}
          isDraft={false}
          nhsNumber={selectedCarePlan.nhsNumber}
          onClose={() => setShowAuditLog(false)}
        />
      )}
    </div>
  );
};

export default ActiveCarePlans;
